import React, { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { stopPageLoading } from '../../../store/reducers/AppSlice';
import PageMetaContext from '../../../shared/hoc/admin-layout/AdminLyout.provider';
import { useSnackbar } from 'notistack';
import { gql, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { TableColumn, WidgetProps } from '../../../shared/types/widget-types';
import ActionMenu from '../../../shared/widgets/action-menu/action-menu';
import { Add, MoreVertOutlined } from '@mui/icons-material';
import { MenuItem } from '@mui/material';
import { useUpdateEffect } from '../../../shared/custom-hooks/useUpdateEffect';
import { responsiveString } from '../../../shared/utils/util-functions';
import TablePageHoc from '../../../shared/hoc/table-list/table-list-hoc';
import { getWidget } from '../../../shared/widgets';
import ConfirmModal from '../../../shared/hoc/modal/confirm-modal';
import AddCategory from './add-category';

type ChildRef = {
    refetchData: (data: any) => void;
    handleDeleterowPagination: () => void
};

const DELETE_CATEGORY_QUERY = gql`mutation DeleteOneCategory($deleteOneCategoryId: ID!) {
  deleteOneCategory(id: $deleteOneCategoryId) {
    success
    errors
  }
}`

const CategoryListPage = () => {
    const [addModalOpen, setAddModalOpen] = React.useState(false);
    const [selectedCategory, setSelectedCategory]: any[] = React.useState(null);
    const [editMode, setEditMode]: any = React.useState(false);
    const [deleteConfirmModal, setDeleteConfirmModal] = React.useState(false);
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = React.useState(1);
    const childRef = useRef<ChildRef | null>(null);
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();
    const pageMeta = useContext(PageMetaContext);
    const [deleteCategory] = useMutation(DELETE_CATEGORY_QUERY);

    const { t } = useTranslation();

    useEffect(() => {
        if (pageMeta) {
            const { setPageName, setBreadcrumbs } = pageMeta;
            setPageName("Category");
        }
        dispatch(stopPageLoading());
    }, [pageMeta]);

    const handleRefetch = () => {
        childRef.current?.refetchData({
            search: searchText ?? ""
        });
    };


    const deleteRow = async () => {
        setDeleteConfirmModal(false);
        if (!selectedCategory) return;
        const variables = {
            deleteOneCategoryId: selectedCategory?.id,
        }

        try {
            const { data } = await deleteCategory({
                variables: variables
            });
            setSelectedCategory(null);

            if (data.deleteOneCategory.errors?.length > 0) {
                console.error('Error:', data.deleteOneCategory.errors);
                enqueueSnackbar(data.deleteOneCategory.errors[0], { variant: "error" });
            } else {

                childRef?.current?.handleDeleterowPagination()
                enqueueSnackbar(t("Category deleted successfully"), { variant: "success" });

            }
        } catch (error) {
            setSelectedCategory(null);
            console.error('Mutation error:', error);
        }
    }


    const TableConfigClubs: TableColumn[] = [
        {
            label: "Action",
            align: "left",
            width: 130,
            accessor: (row: any) => {
                return <>
                    <ActionMenu
                        button={{
                            type: "icon-button",
                            icon: <MoreVertOutlined />,
                            size: 'small',
                            color: 'primary',
                            sx: { color: 'white' }

                        }}
                        children={
                            [
                                <MenuItem onClick={(event: any) => {
                                    setSelectedCategory(row);
                                    setAddModalOpen(true)
                                    event.stopPropagation();
                                    setEditMode(true)
                                }}>{t('Edit')}</MenuItem>,
                                <MenuItem onClick={(event: any) => {
                                    setSelectedCategory(row);
                                    setDeleteConfirmModal(true)
                                    event.stopPropagation();
                                }}>{t('Delete')}</MenuItem>
                            ]
                        }
                    />
                </>
            }
        }
        ,
        {
            label: "Category",
            align: "left",
            accessor: (row: any) => row.categoryName
        }
    ]

    const onSearchTextChange = (event: any) => {
        if (searchText !== event.target.value) {
            setPage(1)
        }
        setSearchText(event.target.value)
    }

    useUpdateEffect(() => {
        handleRefetch();
    }, [searchText], 300)

    const QUERY = `query Query($search: String, $page: Int, $pageSize: Int) {
  getAllCategories(search: $search, page: $page, pageSize: $pageSize) {
    totalCategories
    success
    categories {
      categoryName
      id
    }
    errors
  }
}
        `

    const ActionBarConfig: WidgetProps[] = [

        {
            type: "search-input",
            placeholder: "Search",
            onChange: onSearchTextChange,
            responsive: {
                xs: 12,
                sm: "auto"
            },
            sx: {
                order: {
                    xs: 2,
                    sm: 1
                }
            }
        },
        {
            type: "button",
            label: t(responsiveString({ xs: "Add", sm: "Add Category" })),
            startIcon: <Add />,
            onClick: () => {
                setAddModalOpen(true)
                setEditMode(false)
                setSelectedCategory(null)
            },
            sx: {
                order: {
                    xs: 1,
                    sm: 2
                }
            }
        },
    ]




    const onAddUserModalClose = () => {
        setAddModalOpen(false);
        handleRefetch()
    }




    return (
        <div>
            <TablePageHoc actions={ActionBarConfig} columns={TableConfigClubs} ref={childRef} query={QUERY} params={{
                search: searchText
            }}
                sourceAccessor={(data) => data.getAllCategories.categories} countAccessor={(data) => data.getAllCategories.totalCategories}
                pagination={{
                    enable: true,
                    page: page,
                    onChange: (page) => {
                        setPage(page)
                    }
                }}
            />
            {
                getWidget(
                    {
                        type: "modal",
                        title: selectedCategory ? "Edit Category" : "Add Category",
                        modalOpen: addModalOpen,
                        onClose: () => onAddUserModalClose(),
                        children: <AddCategory category={selectedCategory} editMode={editMode} onSave={() => onAddUserModalClose()} onClose={() => {
                            setAddModalOpen(false)
                        }} />,
                    })
            }
            <ConfirmModal modalOpen={deleteConfirmModal} onClose={() => setDeleteConfirmModal(false)} onConfirm={() => deleteRow()} title={t('Are you sure?')} children={`${t('Do you want to delete the category')} ${selectedCategory?.categoryName}? ${t('Once deleted, this action cannot be undone!')}`} />
    
        </div>
    )
}

export default CategoryListPage